import { resolveUsageLedgerRoute } from '../../../utils/constants/usageLedgerRoutes';

/**
 * ROAR-4943 (ROAR-4899 §4 decision 8) — whether a usage-ledger row deep-links.
 *
 * AC3 qualifies the deep link with **"settled"**: credits are only deducted once a test completes,
 * so a `reserved` row points at a run that has not produced its target yet, and an `expired` one at
 * credits that were released. Linking either would land the user on a half-built or missing screen.
 *
 * This lives next to the ledger rather than inside `usageLedgerRoutes.js` on purpose: row lifecycle
 * is not route knowledge. A future `status` value is a change here, and AC3's settled rule stays
 * visible to anyone reading the ledger component.
 *
 * @param {object} row A ledger row (`status`, `feature_key`, `target_entity_type`, `target_entity_id`).
 * @returns {boolean} True only when the row is settled, has a target, and the pair is mapped.
 */
export const isLedgerRowClickable = (row) => {
  if (row?.status !== 'settled') return false;
  // An empty string is rejected alongside null/undefined: it would build `/app/genpt/tests/`,
  // a real-looking route pointing at nothing. A numeric `0` is allowed — it is still an id.
  const targetId = row?.target_entity_id;
  if (targetId == null || targetId === '') return false;
  return resolveUsageLedgerRoute(row.feature_key, row.target_entity_type) !== null;
};

/**
 * The in-app path a row deep-links to, or null when it is not clickable.
 *
 * Single place that pairs the clickability check with the builder, so a caller cannot navigate
 * using a route it resolved without also honouring the settled rule.
 *
 * @param {object} row A ledger row.
 * @returns {string|null} The path, or null.
 */
export const buildLedgerRowRoute = (row) => {
  if (!isLedgerRowClickable(row)) return null;
  const build = resolveUsageLedgerRoute(row.feature_key, row.target_entity_type);
  return build(row.target_entity_id);
};
